import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { brandGroupIdAtom } from '~/atoms'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useNotify } from '~/hooks/useNotify'
import { useSurveyQueryAvailableBrandGroups } from '~/hooks/useSurveyQuery'
import { exists } from '~/utils'

import {
  isTabulationFetchingAtom,
  isTabulationReadyAtom,
  lastTabulationParamsAtom,
  tabulatedBrandIdsAtom,
  tabulationDataAtom,
  whoAndWhatAwaitIdAtom,
} from '../atoms'

import { transformToNpiDriverData } from './transformToNpiDriverData'
import { transformWhoAndWhatResult } from './transformWhoAndWhatResult'
import { npiDriverDataAtom } from './useNpiDriverTableData'
import { countWhoAndWhatQuery } from './useWhoAndWhatQueries'

export const useWhoAndWhatTabulationQuery = (awaitId: string | null): void => {
  const graphQLRequest = useGraphQLRequest()
  const notify = useNotify()
  const { t } = useTranslation()
  const [, setTabulationData] = useAtom(tabulationDataAtom)
  const [, setNpiDriverData] = useAtom(npiDriverDataAtom)
  const setIsFetching = useSetAtom(isTabulationFetchingAtom)
  const setIsTabulationReady = useSetAtom(isTabulationReadyAtom)
  const setWhoAndWhatAwaitId = useSetAtom(whoAndWhatAwaitIdAtom)
  const setTabulatedBrandIds = useSetAtom(tabulatedBrandIdsAtom)
  const lastTabulationParams = useAtomValue(lastTabulationParamsAtom)
  const lastTabulationParamsRef = useRef(lastTabulationParams)
  lastTabulationParamsRef.current = lastTabulationParams

  const brandGroups = useSurveyQueryAvailableBrandGroups()
  const brandGroupId = useAtomValue(brandGroupIdAtom)
  const brandIdToName = useMemo(() => {
    const map = new Map<
      (typeof brandGroups)[number]['brands'][number]['id'],
      string
    >()
    const group = brandGroups.find((g) => g.id === brandGroupId)
    group?.brands.forEach((brand) => map.set(brand.id, brand.name))

    return map
  }, [brandGroups, brandGroupId])

  const brandIdToNameRef = useRef(brandIdToName)
  brandIdToNameRef.current = brandIdToName

  useEffect(() => {
    if (!exists(awaitId)) {
      return
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let isMounted = true

    const pollIntervalMs = 500
    const timeoutMs = 30000
    const startTime = Date.now()

    const poll = async (): Promise<void> => {
      try {
        if (Date.now() - startTime > timeoutMs) {
          if (isMounted) {
            setIsFetching(false)
            setIsTabulationReady(false)
            setWhoAndWhatAwaitId(null)
            notify({
              content: '集計がタイムアウトしました。再度お試しください。',
              style: 'error',
            })
          }

          return
        }

        const result = await graphQLRequest({
          document: countWhoAndWhatQuery,
          variables: { awaitId },
        })

        if (!isMounted) {
          return
        }

        const queryResult = result.countWhoAndWhat

        if (!exists(queryResult)) {
          setIsFetching(false)
          setIsTabulationReady(false)

          return
        }

        if (queryResult.arrived) {
          if (exists(queryResult.error)) {
            notify({ content: queryResult.error, style: 'error' })
            setIsFetching(false)
            setIsTabulationReady(false)
            setWhoAndWhatAwaitId(null)

            return
          }

          const countWhoAndWhatResult = queryResult.result ?? null

          const transformed = transformWhoAndWhatResult(countWhoAndWhatResult)
          setTabulationData(transformed)

          const npiDriverData = transformToNpiDriverData(
            countWhoAndWhatResult,
            brandIdToNameRef.current,
          )
          setNpiDriverData(npiDriverData)

          setTabulatedBrandIds(lastTabulationParamsRef.current?.brandIds ?? [])
          setIsFetching(false)
          setIsTabulationReady(true)
          setWhoAndWhatAwaitId(null)

          return
        }

        timeoutId = setTimeout(poll, pollIntervalMs)
      } catch {
        if (isMounted) {
          setIsFetching(false)
          setIsTabulationReady(false)
          setWhoAndWhatAwaitId(null)
          // A ClientError's message is the whole response JSON — never show it.
          notify({ content: t('an_error_has_occurred'), style: 'error' })
        }
      }
    }

    poll()

    return () => {
      isMounted = false
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [
    awaitId,
    graphQLRequest,
    notify,
    setIsFetching,
    setIsTabulationReady,
    setTabulationData,
    setNpiDriverData,
    setTabulatedBrandIds,
    setWhoAndWhatAwaitId,
    t,
  ])
}
